// Bước 2 đăng nhập — mã 6 số gửi qua email (thay TOTP từ 19/10/2026). Sáu ô nhập tự nhảy, dán cả mã được;
// "Gửi lại mã" sau thời gian chờ máy chủ báo. Mã sai 5 lần / hết hạn → bấm gửi lại.

import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Spinner } from '@/components/ui/spinner'
import { AuthStage, AuthTitle } from '@/components/auth/AuthStage'
import { AuthError } from '@/components/auth/AuthFields'
import { ApiError } from '@/lib/api'
import { verifyLoginCode, resendLoginCode, getAuthState, homeRouteOf, useAuth } from '@/lib/auth'
import { getHomeRouteForTenant } from '@/lib/permissions'
import { accountFromSession, rememberAccount, takePendingLogin } from '@/lib/rememberedAccounts'

const EMPTY = ['', '', '', '', '', '']

/** Số giây còn phải chờ trước khi gửi lại được (đếm theo đồng hồ, không theo số lần render). */
function useResendCountdown(sentAt: number | undefined, resendAfter: number | undefined): number {
  const calc = () => (sentAt && resendAfter ? Math.max(0, Math.ceil((sentAt + resendAfter * 1000 - Date.now()) / 1000)) : 0)
  const [left, setLeft] = useState(calc)
  useEffect(() => {
    setLeft(calc())
    const t = setInterval(() => setLeft(calc()), 1000)
    return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentAt, resendAfter])
  return left
}

export default function Verify2FaPage() {
  const nav = useNavigate()
  const auth = useAuth()
  const info = auth.loginCode
  const [digits, setDigits] = useState<string[]>(EMPTY)
  const [submitting, setSubmitting] = useState(false)
  const [resending, setResending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** Mã hiện tại đã bị huỷ (sai quá số lần / hết hạn) → chỉ còn cách gửi lại */
  const [needResend, setNeedResend] = useState(false)
  const inputRefs = useRef<Array<HTMLInputElement | null>>([])
  const left = useResendCountdown(info?.sentAt, info?.resendAfter)

  useEffect(() => {
    // Phải đi từ bước mật khẩu (sessionId chỉ nằm trong bộ nhớ — tải lại trang là mất)
    if (!getAuthState().sessionId) nav('/login', { replace: true })
  }, [nav])

  useEffect(() => {
    const t = setTimeout(() => inputRefs.current[0]?.focus(), 0)
    return () => clearTimeout(t)
  }, [info?.sentAt])

  function updateDigit(i: number, v: string) {
    const clean = v.replace(/\D/g, '').slice(-1)
    const next = [...digits]
    next[i] = clean
    setDigits(next)
    setError(null)
    if (clean && i < 5) inputRefs.current[i + 1]?.focus()
    if (next.every((d) => d.length === 1)) void submit(next.join(''))
  }

  function handleKeyDown(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !digits[i] && i > 0) inputRefs.current[i - 1]?.focus()
    else if (e.key === 'ArrowLeft' && i > 0) inputRefs.current[i - 1]?.focus()
    else if (e.key === 'ArrowRight' && i < 5) inputRefs.current[i + 1]?.focus()
  }

  function handlePaste(e: React.ClipboardEvent) {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pasted.length === 6) {
      e.preventDefault()
      setDigits(pasted.split(''))
      void submit(pasted)
    }
  }

  async function submit(code: string) {
    if (submitting) return
    setSubmitting(true)
    try {
      await verifyLoginCode(code)
      // Nhớ tài khoản cho "Đăng nhập nhanh trên máy này" (chuỗi đăng nhập màn Đăng nhập để lại lúc chuyển sang đây)
      const identifier = takePendingLogin()
      if (identifier) {
        const s = getAuthState()
        rememberAccount(accountFromSession({ identifier, shops: s.shops, activeShopId: s.activeShopId, nppAdmin: s.nppAdmin }))
      }
      toast.success('Đăng nhập thành công')
      // Admin NPP vào khu Quản trị NPP; còn lại theo đơn vị đang chọn
      nav(homeRouteOf(getAuthState(), getHomeRouteForTenant), { replace: true })
    } catch (err) {
      const code = err instanceof ApiError ? err.code : ''
      setError((err as Error).message || 'Mã không đúng')
      if (code === 'auth.code_locked' || code === 'auth.code_expired') setNeedResend(true)
      setDigits(EMPTY)
      inputRefs.current[0]?.focus()
    } finally {
      setSubmitting(false)
    }
  }

  async function resend() {
    if (resending || left > 0) return
    setResending(true)
    try {
      const r = await resendLoginCode()
      setNeedResend(false)
      setError(null)
      setDigits(EMPTY)
      toast.success(`Đã gửi mã mới tới ${r.emailMasked}`)
    } catch (err) {
      setError((err as Error).message || 'Không gửi lại được mã')
    } finally {
      setResending(false)
    }
  }

  const locked = needResend || submitting

  return (
    <AuthStage>
      <AuthTitle sub={<>Mã 6 số đã gửi tới <b>{info?.emailMasked || 'email của bạn'}</b>, hiệu lực 10 phút.</>}>Nhập mã xác nhận</AuthTitle>
      <div className="lg-code" onPaste={handlePaste}>
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => { inputRefs.current[i] = el }}
            value={d}
            onChange={(e) => updateDigit(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            inputMode="numeric"
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            maxLength={1}
            disabled={locked}
            aria-label={`Chữ số thứ ${i + 1}`}
            aria-invalid={error ? true : undefined}
            data-filled={d ? '' : undefined}
          />
        ))}
      </div>
      {submitting && <p className="lg-status"><Spinner /> Đang xác nhận…</p>}
      {error && !submitting && <AuthError>{error}</AuthError>}
      <div className="lg-actions">
        <button type="button" onClick={resend} disabled={resending || left > 0} className="lg-link">
          {resending ? <Spinner className="mr-1.5" /> : null}
          {left > 0 ? `Gửi lại mã sau ${left} giây` : 'Gửi lại mã'}
        </button>
        <Link to="/login" replace className="lg-link lg-link-muted">Đăng nhập tài khoản khác</Link>
      </div>
    </AuthStage>
  )
}
